/**
 * The single source of truth for Taytan's UI geometry and type.
 *
 * Two delivery lanes, and which one a token takes is not a preference — it is
 * decided by who reads the value:
 *
 *   Lane A — CSS custom properties, emitted by DesignTokensProvider into a
 *            <style id="taytan-design-tokens"> sink. Used by the 261 makeStyles
 *            sheets, sx props, base.scss, ag-grid-overrides.css, and MUI
 *            styleOverrides (as `var()` strings — emotion and JSS both pass
 *            string values through verbatim).
 *
 *   Lane B — real JavaScript numbers, passed into createAppTheme(palette,
 *            tokens). Live because the theme provider already rebuilds the
 *            theme on state change.
 *
 * The rule: Lane A if the only consumer is a CSS declaration value. The moment
 * JavaScript reads the number — arithmetic, alpha()/darken()/lighten(), AG
 * Grid's row virtualizer, a media-query condition — it must be Lane B.
 *
 * Colours are deliberately NOT here. They already live per-mode in
 * `src/assets/base.scss` (~40 working variables) and in the MUI palette, where
 * several of them are fed to alpha()/darken()/lighten() — colour-math functions
 * return a malformed string when handed `var(--x)`, and emotion then drops the
 * declaration silently. See CSS_VAR (below) for reading those from JS.
 */

// ── Geometry ────────────────────────────────────────────────────────────────

export type RadiusTokens = {
    /** Buttons, toolbar fields, small controls. */
    sm: number;
    /** Inputs, panels, toggle groups. */
    md: number;
    /** Dialogs, cards, large surfaces. */
    lg: number;
};

export type ControlTokens = {
    /** Compact controls inside grids and dense rows. */
    sm: number;
    /** The house control height: buttons, inputs, selects. */
    md: number;
    /** Filter toolbar fields. */
    lg: number;
};

export type FontTokens = {
    xs: number;
    sm: number;
    /** Body / control text. */
    base: number;
    md: number;
    /** Page title (typography.h1). */
    title: number;
};

export type SpaceTokens = {
    s1: number;
    s2: number;
    s3: number;
    s4: number;
    s5: number;
};

export type PageTokens = {
    /** Padding around the whole page (PageLayout root). */
    padding: number;
    /** Gap between body blocks — filter panel, banners, grid. */
    bodyGap: number;
    /** Gap under the page header. */
    headerGap: number;
    /**
     * The action band along the bottom of every page. Reserved whether or not
     * the page has actions to put in it, so the grid ends on the same line app
     * wide — that consistency is the whole point of the band, and it is lost
     * the moment it collapses on the pages that have no buttons.
     */
    actionBarHeight: number;
};

export type NavTokens = {
    width: number;
    itemPaddingY: number;
    itemPaddingX: number;
    /** Added per nesting level. */
    indentStep: number;
    /** Space above a root section heading. */
    sectionGap: number;
};

/** Whether a surface draws a card or sits flat on the page. */
export type SurfaceVariant = 'card' | 'flat';

export type SurfaceTokens = {
    table: SurfaceVariant;
    filter: SurfaceVariant;
};

/**
 * The two colours a tenant is allowed to own. Every other shade in the palette
 * is derived from these at theme build time (lighten/darken), so a tenant sets
 * two values and the whole app follows instead of drifting into a half-rebranded
 * state where buttons moved but chips did not.
 */
export type ColorTokens = {
    /** Buttons, links, focus — the app's working colour. */
    primary: string;
    /** The Taytan highlight: active nav row, stars, emphasis. */
    accent: string;
    /** Search / secondary buttons, some icons and sidebar text (palette.secondary). */
    secondary: string;
    /** The top bar and the dialog title bars (palette.brandHeader). */
    header: string;
    /** The bottom bar, under the sidebar too (palette.brandFooter). */
    footer: string;
    /** Text on the bottom bar — set with `footer`, so a light footer keeps readable text. */
    footerText: string;
};

export type DesignTokens = {
    radius: RadiusTokens;
    control: ControlTokens;
    font: FontTokens;
    space: SpaceTokens;
    page: PageTokens;
    nav: NavTokens;
    surface: SurfaceTokens;
    color: ColorTokens;
};

/**
 * The shipped defaults. Changing a number here restyles every screen that
 * reads tokens — which is the point, and also why each change carries the
 * reasoning that produced it.
 */
export const DEFAULT_TOKENS: DesignTokens = {
    // Four rival radius scales used to coexist — MuiButton 4, shape 8, Dialog
    // 12, MuiPaper 11.2 — which is why nothing in the app quite matched
    // anything else. Three tokens now, subtly rounded rather than square.
    radius: {
        sm: 6, // buttons, toolbar fields
        md: 6, // inputs, panels, toggle groups
        lg: 10 // dialogs, large surfaces
    },
    control: {
        sm: 28,
        md: 32,
        lg: 34 // filter toolbar fields
    },
    font: {
        xs: 11, // helper text — 10px was below legibility
        sm: 12,
        base: 13,
        md: 14, // body1 stays 14 until the outlier list exists
        title: 16 // page title
    },
    space: {
        s1: 4,
        s2: 6,
        s3: 8,
        s4: 12,
        s5: 16
    },
    page: {
        padding: 8,
        // Settled on the Travel File List pilot, then promoted here.
        bodyGap: 10,
        // The title and the filters are one logical section, so they are
        // separated by space rather than a rule.
        headerGap: 12,
        // A 36px contained button with 6px of air above and below. Slightly
        // less than the 52px the old inline `<Box pt={2}>` + button cost, so
        // the pages that already had buttons gain a little grid height rather
        // than losing any.
        actionBarHeight: 48
    },
    nav: {
        // The rendered sidebar width. SIDEBAR_WIDTH in layoutConstants.ts is the
        // fallback for the same number — keep the two in step.
        width: 230,
        itemPaddingY: 7,
        itemPaddingX: 12,
        // One step per level, and large enough to read as a step at a glance:
        // depth is the rail's primary hierarchy signal, with weight and the
        // expand arrows as support. 8px was ambiguous three levels down.
        indentStep: 14,
        sectionGap: 24
    },
    // Flat is what the app ships: one rule dividing filters from data, no card
    // chrome. These must agree with TABLE_SURFACE_DEFAULT / the flat branch in
    // FilterDataFields — TableCard prefers the token over its own constant, so
    // leaving 'card' here silently reinstated the card everywhere and took the
    // separator with it (the rule is a property of the flat class, not a
    // separate element). The Appearance toggle still lets a tenant pick 'card'.
    surface: {
        table: 'flat',
        filter: 'flat'
    },
    color: {
        primary: '#4A6FA5',
        accent: '#ffb01e',
        secondary: '#3B5998',
        header: '#1B2F5B', // Pegasus navy, from the pegasusisrael site header
        // The header's navy with near-white text, as the footer had before it
        // had its own colour.
        footer: '#1B2F5B',
        footerText: '#d1d5de'
    }
};

// ── Lane A: CSS custom property names ───────────────────────────────────────

/**
 * The CSS variable each token is published as. Import these constants instead
 * of typing `'var(--radius-md)'` by hand — a typo in a var name fails silently
 * (the declaration is simply dropped), which is the worst failure mode there
 * is to debug.
 */
export const TOKEN_VARS = {
    radius: { sm: '--radius-sm', md: '--radius-md', lg: '--radius-lg' },
    control: { sm: '--control-h-sm', md: '--control-h-md', lg: '--control-h-lg' },
    font: {
        xs: '--font-xs',
        sm: '--font-sm',
        base: '--font-base',
        md: '--font-md',
        title: '--font-title'
    },
    space: { s1: '--space-1', s2: '--space-2', s3: '--space-3', s4: '--space-4', s5: '--space-5' },
    page: {
        padding: '--page-pad',
        bodyGap: '--page-gap',
        headerGap: '--page-header-gap',
        actionBarHeight: '--page-action-bar-h'
    },
    nav: {
        width: '--nav-width',
        itemPaddingY: '--nav-item-py',
        itemPaddingX: '--nav-item-px',
        indentStep: '--nav-indent-step',
        sectionGap: '--nav-section-gap'
    },
    color: {
        primary: '--brand-primary',
        accent: '--brand-accent',
        secondary: '--brand-secondary',
        header: '--brand-header'
    }
} as const;

/** `var(--x)` reference for a token, with the current default as a fallback. */
export function tokenVar(name: string, fallback: number | string): string {
    return `var(${name}, ${typeof fallback === 'number' ? `${fallback}px` : fallback})`;
}

/**
 * Colour variables already defined per mode in `src/assets/base.scss`,
 * re-exported so JS files stop hand-typing the strings. Not tokens in the sense
 * above — they are owned by base.scss and switch on `body[data-theme]`.
 */
export const CSS_VAR = {
    pageBg: 'var(--page-bg, #f4f5fd)',
    cardBg: 'var(--card-bg, #ffffff)',
    cardBorder: 'var(--card-border, #e2e8f0)',
    textPrimary: 'var(--text-primary, #2c2c2c)',
    textSecondary: 'var(--text-secondary, #6b7280)',
    panelBg: 'var(--panel-bg, #f8fafc)',
    panelBorder: 'var(--panel-border, #e2e8f0)',
    inputBg: 'var(--input-bg, #ffffff)',
    inputBorder: 'var(--input-border, #d7dbe2)',
    inputBorderHover: 'var(--input-border-hover, #bfc5d0)',
    inputText: 'var(--input-text, #334155)',
    inputPlaceholder: 'var(--input-placeholder, #8a94a6)',
    focusRing: 'var(--focus-ring, #4a6fa5)',
    controlMuted: 'var(--control-muted, #cbd5e0)',
    controlLabel: 'var(--control-label, #64748b)',
    inputLabel: 'var(--input-label, #64748b)',
    // Card elevation — defined in tokensToCss below, per mode.
    cardShadow: 'var(--card-shadow, 0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06))',
    cardShadowHover:
        'var(--card-shadow-hover, 0 2px 4px rgba(15, 23, 42, 0.05), 0 4px 12px rgba(15, 23, 42, 0.08))',
    cardShadowDrag:
        'var(--card-shadow-drag, 0 8px 24px rgba(15, 23, 42, 0.16), 0 2px 6px rgba(15, 23, 42, 0.10))'
} as const;

// ── Presets ─────────────────────────────────────────────────────────────────

export type UiScale = 'compact' | 'default' | 'roomy';

/**
 * Per-user scale. A named preset rather than a free-form token blob: a blob
 * makes every support screenshot unreproducible and grows the preferences row
 * without bound.
 */
export const UI_SCALE_DELTA: Record<UiScale, Partial<DesignTokens>> = {
    compact: {
        control: { sm: 26, md: 30, lg: 32 },
        page: { padding: 6, bodyGap: 8, headerGap: 6, actionBarHeight: 40 },
        nav: { ...DEFAULT_TOKENS.nav, itemPaddingY: 4 }
    },
    default: {},
    roomy: {
        control: { sm: 30, md: 36, lg: 40 },
        page: { padding: 12, bodyGap: 16, headerGap: 12, actionBarHeight: 56 },
        nav: { ...DEFAULT_TOKENS.nav, itemPaddingY: 9 }
    }
};

/**
 * Audit mode: every token slammed to an absurd value. Anything on screen that
 * does NOT move is, by definition, not driven by a token — which makes this a
 * more honest outlier detector than any source scan, because it measures what
 * actually rendered.
 */
export const AUDIT_TOKENS: DesignTokens = {
    radius: { sm: 20, md: 20, lg: 20 },
    control: { sm: 48, md: 56, lg: 56 },
    font: { xs: 16, sm: 18, base: 20, md: 22, title: 30 },
    space: { s1: 12, s2: 16, s3: 20, s4: 28, s5: 36 },
    page: { padding: 24, bodyGap: 32, headerGap: 24, actionBarHeight: 96 },
    nav: { width: 320, itemPaddingY: 20, itemPaddingX: 24, indentStep: 24, sectionGap: 40 },
    surface: { table: 'card', filter: 'card' },
    // Audit mode is about geometry; recolouring the app would only make the
    // exaggerated sizes harder to read.
    color: {
        primary: '#4A6FA5',
        accent: '#ffb01e',
        secondary: '#3B5998',
        header: '#1B2F5B',
        footer: '#1B2F5B',
        footerText: '#d1d5de'
    }
};

// ── Merge ───────────────────────────────────────────────────────────────────

/** Deep-merges one level of token groups; later sources win. */
export function mergeTokens(...sources: Array<Partial<DesignTokens> | null | undefined>): DesignTokens {
    return sources.reduce<DesignTokens>((acc, source) => {
        if (!source) return acc;
        return {
            radius: { ...acc.radius, ...source.radius },
            control: { ...acc.control, ...source.control },
            font: { ...acc.font, ...source.font },
            space: { ...acc.space, ...source.space },
            page: { ...acc.page, ...source.page },
            nav: { ...acc.nav, ...source.nav },
            surface: { ...acc.surface, ...source.surface },
            color: { ...acc.color, ...source.color }
        };
    }, DEFAULT_TOKENS);
}

/** The CSS text of the token sink, for the given tokens. */
export function tokensToCss(tokens: DesignTokens): string {
    const px = (n: number) => `${n}px`;
    const decls: string[] = [
        `${TOKEN_VARS.radius.sm}: ${px(tokens.radius.sm)}`,
        `${TOKEN_VARS.radius.md}: ${px(tokens.radius.md)}`,
        `${TOKEN_VARS.radius.lg}: ${px(tokens.radius.lg)}`,
        `${TOKEN_VARS.control.sm}: ${px(tokens.control.sm)}`,
        `${TOKEN_VARS.control.md}: ${px(tokens.control.md)}`,
        `${TOKEN_VARS.control.lg}: ${px(tokens.control.lg)}`,
        `${TOKEN_VARS.font.xs}: ${px(tokens.font.xs)}`,
        `${TOKEN_VARS.font.sm}: ${px(tokens.font.sm)}`,
        `${TOKEN_VARS.font.base}: ${px(tokens.font.base)}`,
        `${TOKEN_VARS.font.md}: ${px(tokens.font.md)}`,
        `${TOKEN_VARS.font.title}: ${px(tokens.font.title)}`,
        `${TOKEN_VARS.space.s1}: ${px(tokens.space.s1)}`,
        `${TOKEN_VARS.space.s2}: ${px(tokens.space.s2)}`,
        `${TOKEN_VARS.space.s3}: ${px(tokens.space.s3)}`,
        `${TOKEN_VARS.space.s4}: ${px(tokens.space.s4)}`,
        `${TOKEN_VARS.space.s5}: ${px(tokens.space.s5)}`,
        `${TOKEN_VARS.page.padding}: ${px(tokens.page.padding)}`,
        `${TOKEN_VARS.page.bodyGap}: ${px(tokens.page.bodyGap)}`,
        `${TOKEN_VARS.page.headerGap}: ${px(tokens.page.headerGap)}`,
        `${TOKEN_VARS.page.actionBarHeight}: ${px(tokens.page.actionBarHeight)}`,
        `${TOKEN_VARS.nav.width}: ${px(tokens.nav.width)}`,
        `${TOKEN_VARS.nav.itemPaddingY}: ${px(tokens.nav.itemPaddingY)}`,
        `${TOKEN_VARS.nav.itemPaddingX}: ${px(tokens.nav.itemPaddingX)}`,
        `${TOKEN_VARS.nav.indentStep}: ${px(tokens.nav.indentStep)}`,
        `${TOKEN_VARS.nav.sectionGap}: ${px(tokens.nav.sectionGap)}`,
        // Published for CSS that cannot reach the MUI theme (ag-grid overrides,
        // base.scss). The theme itself takes these from the tokens directly.
        `${TOKEN_VARS.color.primary}: ${tokens.color.primary}`,
        `${TOKEN_VARS.color.accent}: ${tokens.color.accent}`,
        `${TOKEN_VARS.color.secondary}: ${tokens.color.secondary}`,
        `${TOKEN_VARS.color.header}: ${tokens.color.header}`
    ];

    // The page's one structural rule: filters and actions above, data below.
    // Neutral and translucent on purpose: a tint would read as branded or
    // interactive, and an alpha overlay stays consistent over whatever sits
    // behind it — page background, grid header, card — where a fixed hex can
    // only ever suit one surface.
    const separator = `:root {\n    --separator: rgba(15, 23, 42, 0.10);\n}\nbody[data-theme="dark"] {\n    --separator: rgba(255, 255, 255, 0.12);\n}`;

    // The filter container: neutral, not tinted. A blue-cast panel competes with
    // the data for attention. #fafafa on the #f5f5f5 page is a barely-there
    // lift — the hairline is what actually delineates it, which is the point:
    // the filter area should support the table, not compete with it.
    const filterPanel = `:root {\n    --filter-panel-bg: #fafafa;\n    --filter-panel-border: #ececec;\n}\nbody[data-theme="dark"] {\n    --filter-panel-bg: #1e1e28;\n    --filter-panel-border: #32323f;\n}`;

    // The grid header band, distinct enough from the white rows to read as a
    // header without becoming a coloured bar.
    const gridHeader = `:root {\n    --grid-header-bg: #eff1f5;\n}\nbody[data-theme="dark"] {\n    --grid-header-bg: #26334a;\n}`;

    // Card elevation. Two layers on purpose: a tight contact shadow that seats the
    // card on the page, and a wide diffuse one that gives it depth. A single
    // blurred shadow reads as a grey smudge at any radius large enough to notice.
    //
    // Not a `DesignTokens` field: by the Lane A/Lane B rule at the top of this
    // file, a shadow is only ever a CSS declaration value — no JavaScript does
    // arithmetic on it — so it lives here and gets its dark variant for free.
    // Dark mode drops the alpha and grows the blur: on a dark surface a shadow
    // reads as absence of light, and the light-mode alphas turn into a black halo.
    const cardShadow = `:root {
    --card-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06);
    --card-shadow-hover: 0 2px 4px rgba(15, 23, 42, 0.05), 0 4px 12px rgba(15, 23, 42, 0.08);
    --card-shadow-drag: 0 8px 24px rgba(15, 23, 42, 0.16), 0 2px 6px rgba(15, 23, 42, 0.10);
}
body[data-theme="dark"] {
    --card-shadow: 0 1px 2px rgba(0, 0, 0, 0.28), 0 1px 4px rgba(0, 0, 0, 0.22);
    --card-shadow-hover: 0 2px 6px rgba(0, 0, 0, 0.32), 0 6px 16px rgba(0, 0, 0, 0.28);
    --card-shadow-drag: 0 10px 30px rgba(0, 0, 0, 0.48), 0 3px 8px rgba(0, 0, 0, 0.32);
}`;

    // The nav tint keeps Taytan's accent but quietly, and needs a touch more
    // presence on the dark rail to read at all.
    // Each pair states the shipped orange first and then the same value mixed
    // from the tenant's accent. A browser without color-mix drops the second
    // declaration and keeps the first, so the tint degrades to Taytan's rather
    // than to nothing.
    const navTint = `:root {\n    --nav-active-bg: rgba(255, 176, 30, 0.12);\n    --nav-active-bg: color-mix(in srgb, ${tokens.color.accent} 12%, transparent);\n    --nav-hover-bg: rgba(255, 176, 30, 0.06);\n    --nav-hover-bg: color-mix(in srgb, ${tokens.color.accent} 6%, transparent);\n}\nbody[data-theme="dark"] {\n    --nav-active-bg: rgba(255, 176, 30, 0.20);\n    --nav-active-bg: color-mix(in srgb, ${tokens.color.accent} 20%, transparent);\n    --nav-hover-bg: rgba(255, 176, 30, 0.10);\n    --nav-hover-bg: color-mix(in srgb, ${tokens.color.accent} 10%, transparent);\n}`;

    // `:root` and not `html[style]`: an inline style on the root element
    // out-specifies `body[data-theme="dark"]` in base.scss and would freeze
    // every colour at its light value. This sheet is appended after base.scss,
    // so equal specificity resolves in its favour by document order.
    // The rail's text hierarchy. Three greys a step apart, not a black-to-grey
    // drop: depth, weight and the expand arrows carry the structure, and colour
    // only nudges it. A section heading is muted on purpose — it is a label,
    // not a link — while the items under it get progressively quieter.
    const navText = `:root {\n    --nav-section-text: #5c6674;\n    --nav-parent-text: #3e4653;\n    --nav-leaf-text: #5a6371;\n}\nbody[data-theme="dark"] {\n    --nav-section-text: #8b96a6;\n    --nav-parent-text: #dde3ec;\n    --nav-leaf-text: #b0b9c6;\n}`;

    return `:root {\n    ${decls.join(';\n    ')};\n}\n${separator}\n${filterPanel}\n${gridHeader}\n${cardShadow}\n${navTint}\n${navText}`;
}
